import React from 'react';
import { get } from 'lodash';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getModelState } from 'client/data/luckdragon/model';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import {
  VehicleModel,
  buildVehiclePath,
  buildPopularStylesPath,
  buildMakeModelDefaultYear,
  FeatureSpecsPaths,
} from 'client/data/models/vehicle-v2';
import { buildReviewPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { CostToOwnModel, buildTcoPath } from 'client/data/models/cost-to-own';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { getParamsWithMakes } from 'client/utils/router-params';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { BUILD_PRICE_AD } from 'client/site-modules/shared/components/native-ad/utils/constants';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { PageModel } from 'client/data/models/page';
import { setAdsListToPreload } from 'client/site-modules/shared/components/native-ad/utils/utils';
import { setCriticalCss } from 'client/actions/critical-css';
import { getDisplayedStyleId, isTcoDataAvailable } from 'client/site-modules/cost-to-own/utils/tco-utils';

import { CostToOwn, PAGE_DISPLAY_NAME, URL_PATH } from './cost-to-own';

export const pageOptions = {
  name: 'model_core_tco',
  category: 'model_core',
  preload: (store, props) => {
    const params = getParamsWithMakes(props);

    const preloader = new VehiclePreloader(store, { ...props, params });
    preloader.resolve(buildMakeModelDefaultYear(params), VehicleModel);

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
      const vehicleParams = getParamsFromVehicle(makeModelSubmodelYear);
      const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);

      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_model_core_tco`,
          categoryName: `${isUsed ? 'used' : 'new'}_model_core`,
        })
      );

      preloader.resolve(buildPopularStylesPath(params), VehicleModel);
      preloader.resolve(buildTcoPath(params), CostToOwnModel);
      // hero photo
      preloader.resolve(buildReviewPath(params), EditorialReviewModel);
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(preloader, store.getState(), [BUILD_PRICE_AD.AD_NAME], vehiclePageContext);

      preloader.resolve(
        buildAllPhotosPath({
          ...vehicleParams,
          pageNum: 1,
          submodel: makeModelSubmodelYear.submodels.name,
          imageSize: 600,
        }),
        PhotoflipperModel
      );

      return preloader.load().then(modelStateInstance => {
        const tcoStylesData = modelStateInstance.get(buildTcoPath(params), CostToOwnModel);
        const styles = modelStateInstance.get(buildPopularStylesPath(params), VehicleModel);

        const preselectedStyleId = getDisplayedStyleId({ location: get(props, 'location', {}), styles, tcoStylesData });

        preloader.resolve(FeatureSpecsPaths.buildStyleAttributesPath(preselectedStyleId), VehicleModel);

        return preloader.load().then(stateModel => {
          const styleAttributes = stateModel.get(
            FeatureSpecsPaths.buildStyleAttributesPath(preselectedStyleId),
            VehicleModel
          );

          store.dispatch(
            setCriticalCss({
              hasTcoContent: isTcoDataAvailable({ tcoStylesData, styleId: preselectedStyleId }),
              isEvStyle: get(styleAttributes, 'pluginElectric') || get(styleAttributes, 'electric'),
            })
          );

          return preloader.load();
        });
      });
    });
  },
  seo: (state, props) => {
    const params = getParamsWithMakes(props);
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);
    const seoObject = {};

    if (makeModelSubmodelYear) {
      const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params, usedPrefix: '' });

      seoObject.jsonld = [
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear,
            params,
            defaultYear,
            pageDisplayName: PAGE_DISPLAY_NAME,
            urlPath: getUrlPathFromParams(params, URL_PATH),
            includeLastLink: true,
          })
        ),
      ];

      seoObject.title = `${displayVehicle}: True Cost to Own | Edmunds`;
      seoObject.canonical = `https://www.edmunds.com/${params.make}/${params.model}/${params.year}/cost-to-own/`;
      seoObject.description = `View detailed ownership costs for the ${displayVehicle} on Edmunds. The ${displayVehicle} True Cost to Own includes depreciation, taxes, financing, fuel costs, insurance, maintenance, repairs, and tax credits over the span of 5 years of ownership.`;
      seoObject.keywords = `${displayVehicle} true cost to own, ${displayVehicle}, car true cost to own, edmunds.com`;
    }

    return seoObject;
  },
  component: props => <CostToOwn {...props} params={getParamsWithMakes(props)} />,
  options: {
    hasVehicleContext: true,
  },
};

export default pageDefinition(pageOptions);
